/**
 * The shell: top bar, a small path router (/app, /app/people/:upn, /app/programs, /app/programs/:id,
 * /app/portfolio, /app/vips, /app/integrations, /app/ops), data loading and the actions. The screens themselves are pure and live in ./screens.
 */
import { useEffect, useRef, useState } from "preact/hooks";
import type { ComponentChildren } from "preact";
import type { PortfolioLine, PortfolioView, ProgramView, RecordAction, RecordView, RosterPageView, UnitView } from "@trellis/core";
import { LEAD_ROLES } from "@trellis/core";
import { api, ApiError } from "./api.js";
export { navigate } from "./components/link.js";
import { Link, navigate } from "./components/link.js";
import { useHashLanding } from "./components/anchor.js";
import { Palette, type Command } from "./components/palette.js";
import { TopBar, canSeeOps, canSeePrograms, canSeeUnits, contractOf, type Contract, type Me, type UnitHeld } from "./components/topbar.js";
import { ListScreen, type ListKind } from "./screens/lists.js";
import type { Session } from "./auth.js";
import { RecordScreen, type ActionBody, type Person } from "./screens/record.js";
import { ProgramScreen, type AdminProps, type PickerModel } from "./screens/program.js";
import { ModelScreen, type CadenceEdit, type IdentitiesView } from "./screens/model.js";
import { PortfolioScreen } from "./screens/portfolio.js";
import { VipsScreen, type VipLine } from "./screens/vips.js";
import { TopicsScreen, type TopicLine } from "./screens/topics.js";
import { InitiativeScreen, InitiativesScreen, type InitiativeDetail, type InitiativeSummary } from "./screens/initiatives.js";
import { IntegrationsScreen, type IntegrationEdit, type IntegrationLine } from "./screens/integrations.js";
import { StructureScreen, type StructurePage } from "./screens/structure.js";
import { AccountsPanel, RosterPageScreen, personHref, type LinkedAccount, type MappedAccount } from "./screens/roster-page.js";
import { PersonIdentityScreen, type PersonIdentityView } from "./screens/person-identity.js";
import { UnitWeekScreen } from "./screens/unit-week.js";
import { DraftScreen, type DraftView } from "./screens/draft.js";
import { OpsScreen, type OpsScreenView } from "./screens/ops.js";

export { canSeeOps, canSeePrograms, canSeeUnits } from "./components/topbar.js";
export type { Me, UnitHeld } from "./components/topbar.js";

interface ProgramSummary { id: string; name: string; profile: string; roleIds: string[]; canView: boolean }
export type PortfolioResponse = Omit<PortfolioView, "programs"> & { viewer: string; programs: (PortfolioLine & { canView: boolean })[] };

export type Route = { kind: "record"; upn?: string; focusId?: string } | { kind: "list"; list: ListKind; upn?: string } | { kind: "programs" } | { kind: "program"; id: string } | { kind: "structure"; id: string } | { kind: "model"; id: string } | { kind: "roster-page"; id: string } | { kind: "person-identity"; id: string; upn: string } | { kind: "unit-week"; id: string; unitId: string } | { kind: "units" } | { kind: "draft"; id: string; artifactRowId: string } | { kind: "portfolio" } | { kind: "vips" } | { kind: "topics" } | { kind: "initiatives" } | { kind: "initiative"; id: string } | { kind: "integrations" } | { kind: "ops" } | { kind: "missing"; path: string };

/** `?upn=` on a list route: whose record the list belongs to. Absent means the reader's own. */
export function upnOf(search: string): string | undefined {
  const v = new URLSearchParams(search.startsWith("?") ? search : `?${search}`).get("upn");
  return v ? v.toLowerCase() : undefined;
}

/** `?account=<integrationId>:<externalId>` on a person's page: the account a lead arrived from. */
export function accountOf(search: string): string | undefined {
  const v = new URLSearchParams(search.startsWith("?") ? search : `?${search}`).get("account");
  return v && v.includes(":") ? v : undefined;
}

export function focusIdOf(search: string): string | undefined {
  const id = new URLSearchParams(search.startsWith("?") ? search : `?${search}`).get("c");
  return id || undefined;
}

export function parseRoute(pathname: string): Route {
  const parts = pathname.replace(/^\/app\/?/, "").split("/").filter(Boolean).map(decodeURIComponent);
  if (parts.length === 0) return { kind: "record" };
  if (parts[0] === "i" && parts[1]) return { kind: "record", focusId: parts[1] };
  if (parts[0] === "people" && parts[1]) return { kind: "record", upn: parts[1].toLowerCase() };
  if (parts.length === 1 && (parts[0] === "commitments" || parts[0] === "candidates" || parts[0] === "cadences")) return { kind: "list", list: parts[0] };
  if (parts[0] === "units" && parts.length === 1) return { kind: "units" };
  if (parts[0] === "programs" && parts.length === 1) return { kind: "programs" };
  if (parts[0] === "programs" && parts[1] && parts[2] === "drafts" && parts[3]) return { kind: "draft", id: parts[1], artifactRowId: parts[3] };
  if (parts[0] === "programs" && parts[1] && parts[2] === "structure") return { kind: "structure", id: parts[1] };
  if (parts[0] === "programs" && parts[1] && parts[2] === "model") return { kind: "model", id: parts[1] };
  if (parts[0] === "programs" && parts[1] && parts[2] === "roster" && parts[3]) return { kind: "person-identity", id: parts[1], upn: parts[3].toLowerCase() };
  if (parts[0] === "programs" && parts[1] && parts[2] === "roster") return { kind: "roster-page", id: parts[1] };
  if (parts[0] === "programs" && parts[1] && parts[2] === "units" && parts[3]) return { kind: "unit-week", id: parts[1], unitId: parts[3] };
  if (parts[0] === "programs" && parts[1]) return { kind: "program", id: parts[1] };
  if (parts[0] === "portfolio" && parts.length === 1) return { kind: "portfolio" };
  if (parts[0] === "vips" && parts.length === 1) return { kind: "vips" };
  if (parts[0] === "topics" && parts.length === 1) return { kind: "topics" };
  if (parts[0] === "initiatives" && parts.length === 1) return { kind: "initiatives" };
  if (parts[0] === "initiatives" && parts.length === 2) return { kind: "initiative", id: decodeURIComponent(parts[1]!) };
  if (parts[0] === "integrations" && parts.length === 1) return { kind: "integrations" };
  if (parts[0] === "ops" && parts.length === 1) return { kind: "ops" };
  return { kind: "missing", path: pathname };
}





function Hidden() {
  return <div class="state">Nothing at this address.</div>;
}


/**
 * Where somebody lands when they open Trellis with no page in mind. A portfolio lead
 * opens on the portfolio, a lead of exactly one contract on that contract, and everybody
 * else on their own record, which is what the product is for.
 *
 * Only on the first load at a bare `/app`: clicking "Your record" later must mean the
 * record, not another bounce (24 September).
 */
export function landingFor(me: Me): string | undefined {
  if (me.portfolio) return "/app/portfolio";
  const leads = me.programs.filter((p) => p.roleIds.some((r) => LEAD_ROLES.includes(r)));
  if (leads.length === 1) return `/app/programs/${encodeURIComponent(leads[0]!.id)}`;
  if (leads.length > 1) return "/app/programs";
  return undefined;
}

export function App({ session }: { session: Session }) {
  const [route, setRoute] = useState<Route>(() => parseRoute(location.pathname));
  const [listUpn, setListUpn] = useState(() => (typeof location !== "undefined" ? upnOf(location.search) : undefined));
  const [focusId, setFocusId] = useState(() => typeof location !== "undefined" ? focusIdOf(location.search) : undefined);
  const [me, setMe] = useState<Me | undefined>(undefined);
  const [meError, setMeError] = useState<string | undefined>(undefined);
  // A sponsor is not always on the roster of a contract they can open, so `/api/me` does
  // not always name it. The page that does load the name says so, and the bar keeps it.
  const [contract, setContract] = useState<Contract | undefined>(undefined);
  useEffect(() => {
    const on = () => { setRoute(parseRoute(location.pathname)); setFocusId(focusIdOf(location.search)); setListUpn(upnOf(location.search)); };
    addEventListener("popstate", on);
    return () => removeEventListener("popstate", on);
  }, []);
  const loadMe = () => api<Me>(session, "/api/me").then(setMe, (e: Error) => setMeError(e.message));
  useEffect(() => { void loadMe(); }, [session]);
  // Once, on the first load, and only at a bare /app.
  const landed = useRef(false);
  useEffect(() => {
    if (landed.current || !me) return;
    landed.current = true;
    if (!/^\/app\/?$/.test(location.pathname) || location.search) return;
    const to = landingFor(me);
    if (to) { history.replaceState({}, "", to); setRoute(parseRoute(to)); }
  }, [me]);

  return (
    <div class="frame">
      <TopBar route={route} me={me} session={session} contract={contract} />
      <Palette commands={commandsFor(me)} />
      {meError
        ? <div class="state error">Sign-in did not resolve to a person.<div class="sub">{meError}</div></div>
        : route.kind === "record" ? <RecordPage key={route.upn ?? "me"} session={session} upn={route.upn} me={me} onMeChanged={loadMe} focusId={route.focusId ?? focusId} />
        : route.kind === "list" ? <ListPage key={`${route.list}:${listUpn ?? "me"}`} session={session} list={route.list} upn={listUpn} onMeChanged={loadMe} />
        : route.kind === "programs" ? <ProgramsPage session={session} />
        : route.kind === "units" ? <UnitsPage me={me} />
        : route.kind === "program" ? <ProgramPage key={route.id} session={session} id={route.id} me={me} onContract={setContract} onMeChanged={loadMe} />
        : route.kind === "structure" ? <StructureRoute key={route.id} session={session} id={route.id} me={me} onContract={setContract} onMeChanged={loadMe} />
        : route.kind === "model" ? <ModelRoute key={route.id} session={session} id={route.id} onContract={setContract} />
        : route.kind === "roster-page" ? <RosterRoute key={route.id} session={session} id={route.id} me={me} onContract={setContract} />
        : route.kind === "person-identity" ? <PersonIdentityRoute key={`${route.id}:${route.upn}`} session={session} id={route.id} upn={route.upn} onContract={setContract} />
        : route.kind === "unit-week" ? <UnitWeekRoute key={`${route.id}:${route.unitId}`} session={session} id={route.id} unitId={route.unitId} me={me} onMeChanged={loadMe} />
        : route.kind === "draft" ? <DraftPage key={route.artifactRowId} session={session} id={route.id} artifactRowId={route.artifactRowId} />
        : route.kind === "portfolio" ? <PortfolioPage session={session} />
        : route.kind === "vips" ? <VipsPage session={session} />
        : route.kind === "topics" ? <TopicsPage session={session} />
        : route.kind === "initiatives" ? <InitiativesPage session={session} me={me} />
        : route.kind === "initiative" ? <InitiativePage session={session} id={route.id} me={me} />
        : route.kind === "integrations" ? <IntegrationsPage session={session} />
        : route.kind === "ops" ? <OpsPage session={session} />
        : <div class="state">Nothing at this address.<div class="sub"><Link href="/app">Your record</Link>{canSeePrograms(me) ? <> · <Link href="/app/programs">Programs</Link></> : null}{canSeeUnits(me) ? <> · <Link href="/app/units">Your units</Link></> : null}</div></div>}
    </div>
  );
}

/**
 * What the palette can reach: everything the reader can already open. It is built from
 * `/api/me`, so it never offers a programme or a person the reader would be refused.
 */
function commandsFor(me: Me | undefined): Command[] {
  const out: Command[] = [
    { id: "record", what: "Your record", where: "Everything on you", href: "/app", keywords: "home me mine" },
    { id: "commitments", what: "Commitments", where: "The full list", href: "/app/commitments", keywords: "open work" },
    { id: "candidates", what: "Candidates", where: "Waiting on you", href: "/app/candidates", keywords: "confirm unconfirmed" },
    { id: "cadences", what: "Cadences", where: "What runs on a schedule", href: "/app/cadences", keywords: "report meeting weekly" },
    { id: "initiatives", what: "Initiatives", where: "Across contracts", href: "/app/initiatives", keywords: "quarter push" },
  ];
  if (!me) return out;
  if (canSeePrograms(me)) out.push({ id: "programs", what: "Programs", where: "Every contract you hold a role on", href: "/app/programs" });
  if (me.portfolio) out.push({ id: "portfolio", what: "Portfolio", where: "Every contract, side by side", href: "/app/portfolio" });
  if (canSeeOps(me)) out.push({ id: "ops", what: "Operations", where: "What the pipeline did", href: "/app/ops", keywords: "health metrics ingestion backlog dropped skipped funnel" });
  if (me.isSponsor) {
    out.push({ id: "vips", what: "VIPs", where: "Whose mail is always read", href: "/app/vips" });
    out.push({ id: "topics", what: "Topics", where: "What is always read", href: "/app/topics" });
    out.push({ id: "instances", what: "Instances", where: "Linked systems", href: "/app/integrations" });
  }
  // Being on a programme's roster does not open its pages: `/api/programs/:id/view`
  // answers to a role holder or a sponsor. Offering the rest a link is offering a 403.
  for (const p of me.programs.filter((x) => me.isSponsor || x.roleIds.length > 0)) {
    out.push({ id: `p:${p.id}`, what: p.name, where: "Program, this week", href: `/app/programs/${encodeURIComponent(p.id)}`, keywords: "contract program" });
    out.push({ id: `s:${p.id}`, what: `${p.name} · Structure`, where: "CLINs, workstreams, teams", href: `/app/programs/${encodeURIComponent(p.id)}/structure`, keywords: "tree org chart clin" });
    out.push({ id: `r:${p.id}`, what: `${p.name} · Roster`, where: "Who is on it", href: `/app/programs/${encodeURIComponent(p.id)}/roster`, keywords: "people staffing hours" });
    out.push({ id: `m:${p.id}`, what: `${p.name} · Delivery model`, where: "Version, profile, parameters, cadences", href: `/app/programs/${encodeURIComponent(p.id)}/model`, keywords: "model profile parameters cadence" });
  }
  for (const u of me.units ?? []) {
    out.push({ id: `u:${u.unitId}`, what: u.unitName, where: `${u.kind === "team" ? "Team" : "Workstream"} on ${u.programName}`, href: `/app/programs/${encodeURIComponent(u.programId)}/units/${encodeURIComponent(u.unitId)}`, keywords: "unit workstream team" });
  }
  for (const r of me.reports) {
    out.push({ id: `who:${r.upn}`, what: r.name, where: "Their record", href: `/app/people/${encodeURIComponent(r.upn)}`, keywords: `report ${r.upn}` });
  }
  return out;
}

function ListPage({ session, list, upn, onMeChanged }: { session: Session; list: ListKind; upn: string | undefined; onMeChanged: () => Promise<void> }) {
  const [record, setRecord] = useState<RecordView | undefined>(undefined);
  const [error, setError] = useState<{ status: number; message: string } | undefined>(undefined);
  const path = upn ? `/api/people/${encodeURIComponent(upn)}/record?days=90` : "/api/me/record?days=90";
  const load = () => api<RecordView>(session, path).then((r) => { setRecord(r); setError(undefined); }, (e: ApiError) => setError({ status: e.status ?? 0, message: e.message }));
  useEffect(() => { void load(); }, [path]);
  const [notice, setNotice] = useState<{ text: string; error?: boolean } | undefined>(undefined);
  const onAction = async (id: string, action: RecordAction, body?: ActionBody) => {
    try {
      const r = await api<{ text: string }>(session, `/api/commitments/${encodeURIComponent(id)}/${action}`, { method: "POST", body: body ?? {} });
      setNotice({ text: r.text });
      await Promise.all([load(), onMeChanged()]);
    } catch (e) {
      setNotice({ text: e instanceof Error ? e.message : String(e), error: true });
    }
  };
  const peopleOf = async (programId: string): Promise<Person[]> => (await api<{ people: Person[] }>(session, `/api/programs/${encodeURIComponent(programId)}/people`)).people;
  if (error) return error.status === 403 || error.status === 404 ? <Hidden /> : <div class="state error">That list did not load.<div class="sub">{error.message}</div></div>;
  if (!record) return <div class="state muted">Reading the record…</div>;
  return <div class="body"><ListScreen kind={list} record={record} onAction={onAction} peopleOf={peopleOf} notice={notice} /></div>;
}

function RecordPage({ session, upn, me, onMeChanged, focusId }: { session: Session; upn: string | undefined; me: Me | undefined; onMeChanged: () => Promise<void>; focusId?: string | undefined }) {
  const [record, setRecord] = useState<RecordView | undefined>(undefined);
  const [error, setError] = useState<{ status: number; message: string } | undefined>(undefined);
  const [notice, setNotice] = useState<{ text: string; error?: boolean } | undefined>(undefined);
  const [myVips, setMyVips] = useState<{ personal: VipLine[]; inherited: (VipLine & { relation: string })[] } | undefined>(undefined);
  const path = upn ? `/api/people/${encodeURIComponent(upn)}/record?days=90` : "/api/me/record?days=90";
  const load = () => api<RecordView>(session, path).then((r) => { setRecord(r); setError(undefined); }, (e: ApiError) => setError({ status: e.status ?? 0, message: e.message }));
  useEffect(() => { void load(); }, [path]);
  useEffect(() => {
    if (upn) { setMyVips(undefined); return; }
    api<{ personal: VipLine[]; inherited: (VipLine & { relation: string })[] }>(session, "/api/me/vips").then(setMyVips, () => setMyVips(undefined));
  }, [session, upn]);
  const saveVips = async (personal: VipLine[]) => {
    const next = await api<{ personal: VipLine[]; inherited: (VipLine & { relation: string })[] }>(session, "/api/me/vips", { method: "PUT", body: { entries: personal } });
    setMyVips(next);
    setNotice({ text: "Your VIPs are saved." });
  };

  const attempt = async (fn: () => Promise<{ text: string }>) => {
    try {
      const r = await fn();
      setNotice({ text: r.text });
      // The row changed, so the count in the chrome changed with it.
      await Promise.all([load(), onMeChanged()]);
    } catch (e) { setNotice({ text: e instanceof Error ? e.message : String(e), error: true }); }
  };
  const onAction = (id: string, action: RecordAction, body?: ActionBody) => attempt(() => api<{ text: string }>(session, `/api/commitments/${encodeURIComponent(id)}/${action}`, { method: "POST", body: body ?? {} }));
  const onAdd = (programId: string, title: string, dueDate: string) => attempt(() => api<{ text: string }>(session, `/api/programs/${encodeURIComponent(programId)}/commitments`, { method: "POST", body: { title, dueDate } }));
  const onPropose = (programId: string, title: string, dueDate: string) => attempt(() => api<{ text: string }>(session, `/api/people/${encodeURIComponent(upn!)}/proposals`, { method: "POST", body: { programId, title, dueDate } }));
  const peopleOf = async (programId: string): Promise<Person[]> => (await api<{ people: Person[] }>(session, `/api/programs/${encodeURIComponent(programId)}/people`)).people;
  const initiativesOf = async (programId: string) => (await api<{ initiatives: { id: string; name: string }[] }>(session, `/api/programs/${encodeURIComponent(programId)}/initiatives`)).initiatives;
  const onInitiative = async (commitmentId: string, initiativeId: string | null, milestone: boolean) => {
    await attempt(async () => {
      if (initiativeId) {
        await api(session, `/api/initiatives/${encodeURIComponent(initiativeId)}/commitments/${encodeURIComponent(commitmentId)}`, { method: "PUT", body: { milestone } });
        return { text: milestone ? "On the initiative, as a milestone." : "On the initiative." };
      }
      // Taking it off needs the one it is on; the line carries it.
      const on = record?.lines.concat(record.candidates).find((l) => l.id === commitmentId)?.initiativeId;
      if (!on) return { text: "It was not on one." };
      await api(session, `/api/initiatives/${encodeURIComponent(on)}/commitments/${encodeURIComponent(commitmentId)}`, { method: "DELETE" });
      return { text: "Taken off." };
    });
  };
  // PUT, because the API's own `:action` route owns every two-segment POST under a commitment.
  const onResemblance = (commitmentId: string, of: string, verdict: "repeats" | "different") =>
    attempt(() => api<{ text: string }>(session, `/api/commitments/${encodeURIComponent(commitmentId)}/resemblance`, { method: "PUT", body: { of, verdict } }));
  const [myTopics, setMyTopics] = useState<TopicLine[] | undefined>(undefined);
  const loadMyTopics = () => api<{ entries: TopicLine[] }>(session, "/api/me/topics").then((r) => setMyTopics(r.entries), () => setMyTopics(undefined));
  useEffect(() => { if (!upn) void loadMyTopics(); }, [session, upn]);
  const saveMyTopics = async (next: TopicLine[]) => {
    const r = await api<{ entries: TopicLine[] }>(session, "/api/me/topics", { method: "PUT", body: { topics: next } });
    setMyTopics(r.entries);
  };
  const onTimezone = async (tz: string | null) => { await attempt(async () => { await api(session, "/api/me/timezone", { method: "PUT", body: { timezone: tz } }); const zone = tz ?? "America/New_York"; return { text: `Cards at 06:00, 12:00, 16:00 ${zone}.` }; }); };

  if (error) {
    if (error.status === 403 || error.status === 404) return <Hidden />;
    return <div class="state error">The record did not load.<div class="sub">{error.message}</div></div>;
  }
  if (!record) return <div class="state muted">Reading the record…</div>;
  const mine = record.viewerIsOwner;
  return (
    <>
      {!upn && me && me.reports.length > 0 && (
        <div class="context-strip">Your reports: {me.reports.map((r, i) => <span key={r.upn}>{i ? " · " : ""}<Link href={`/app/people/${encodeURIComponent(r.upn)}`} class="link">{r.name}</Link></span>)}</div>
      )}
      {!upn && me && (me.units?.length ?? 0) > 0 && (
        <div class="context-strip">Your units: {me.units!.map((u, i) => <span key={u.unitId}>{i ? " · " : ""}<Link href={`/app/programs/${encodeURIComponent(u.programId)}/units/${encodeURIComponent(u.unitId)}`} class="link">{u.unitName}</Link> <span class="muted">({u.programName})</span></span>)}</div>
      )}
      <RecordScreen
        record={record} notice={notice} focusId={focusId}
        onAction={mine ? onAction : undefined} onAdd={mine ? onAdd : undefined} peopleOf={mine ? peopleOf : undefined}
        initiativesOf={mine ? initiativesOf : undefined} onInitiative={mine ? onInitiative : undefined}
        onResemblance={mine ? onResemblance : undefined}
        onPropose={!mine ? onPropose : undefined}
        timezone={mine ? me?.timezone ?? null : undefined} onTimezone={mine ? onTimezone : undefined}
        vips={mine && myVips ? myVips : undefined} onVips={mine ? saveVips : undefined}
        topics={mine && myTopics ? myTopics : undefined} onTopics={mine ? saveMyTopics : undefined}
      />
    </>
  );
}

function UnitsPage({ me }: { me: Me | undefined }) {
  const units = me?.units ?? [];
  if (!me) return <div class="state muted">Reading your units…</div>;
  if (units.length === 0) return <div class="state">Nothing at this address.</div>;
  return (
    <main class="main">
      <h1 class="headline" style="margin:0">Your units</h1>
      <ul class="plain-list" style="margin-top: 22px; max-width: 720px;">
        {units.map((u) => (
          <li key={u.unitId}>
            <Link href={`/app/programs/${encodeURIComponent(u.programId)}/units/${encodeURIComponent(u.unitId)}`} class="name">{u.unitName}</Link>
            <div class="s">{u.programName} · {u.roleIds.join(", ")}</div>
          </li>
        ))}
      </ul>
    </main>
  );
}

function ProgramsPage({ session }: { session: Session }) {
  const [programs, setPrograms] = useState<ProgramSummary[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  useEffect(() => { api<{ programs: ProgramSummary[] }>(session, "/api/programs").then((r) => setPrograms(r.programs), (e: Error) => setError(e.message)); }, [session]);
  if (error) return <div class="state error">Programs did not load.<div class="sub">{error}</div></div>;
  if (!programs) return <div class="state muted">Reading your programs…</div>;
  if (programs.length === 0) return <div class="state">No programs yet.</div>;
  return (
    <main class="main">
      <h1 class="headline" style="margin:0">Your programs</h1>
      <ul class="plain-list" style="margin-top: 22px; max-width: 720px;">
        {programs.map((p) => (
          <li key={p.id}>
            <Link href={`/app/programs/${encodeURIComponent(p.id)}`} class="name">{p.name}</Link>
            {p.roleIds.length > 0 && <div class="s">{p.roleIds.join(", ")}</div>}
          </li>
        ))}
      </ul>
    </main>
  );
}

function ProgramPage({ session, id, me, onContract, onMeChanged }: { session: Session; id: string; me: Me | undefined; onContract: (c: Contract) => void; onMeChanged: () => Promise<void> }) {
  const [view, setView] = useState<ProgramView | undefined>(undefined);
  const [model, setModel] = useState<PickerModel | undefined>(undefined);
  const [error, setError] = useState<{ status: number; message: string } | undefined>(undefined);
  const [notice, setNotice] = useState<{ text: string; error?: boolean } | undefined>(undefined);
  const [card, setCard] = useState<StructurePage["card"] | undefined>(undefined);
  const [instances, setInstances] = useState<{ id: string; displayName: string; kind: string }[]>([]);
  const load = () => api<ProgramView>(session, `/api/programs/${encodeURIComponent(id)}/view`).then((v) => { setView(v); setError(undefined); }, (e: ApiError) => setError({ status: e.status ?? 0, message: e.message }));
  useEffect(() => { void load(); }, [session, id]);
  useEffect(() => { if (view) onContract({ id, name: view.header.name }); }, [view]);
  // A cause on the portfolio links to a panel on this page, and the panel is not there to
  // land on until the view is.
  useHashLanding(!!view);
  const canAdmin = !!me && (me.isSponsor || (me.programs.find((p) => p.id === id)?.roleIds.length ?? 0) > 0);
  useEffect(() => { if (canAdmin && !model) api<PickerModel>(session, "/api/model").then(setModel, () => undefined); }, [canAdmin]);
  useEffect(() => { if (canAdmin) api<StructurePage>(session, `/api/programs/${encodeURIComponent(id)}/structure`).then((s) => setCard(s.card), () => setCard(undefined)); }, [canAdmin, session, id]);
  useEffect(() => {
    if (!canAdmin) return;
    api<{ integrations: { id: string; displayName: string; kind: string }[] }>(session, "/api/integrations").then((r) => {
      setInstances(r.integrations);
    }, () => setInstances([]));
  }, [canAdmin, session]);

  const save = async (fn: () => Promise<unknown>, text: string) => {
    try { await fn(); setNotice({ text }); await Promise.all([load(), onMeChanged()]); }
    catch (e) { setNotice({ text: e instanceof Error ? e.message : String(e), error: true }); }
  };
  const base = `/api/programs/${encodeURIComponent(id)}`;
  const admin: AdminProps | undefined = canAdmin && model && me ? {
    model, isSponsor: me.isSponsor, notice,
    onRole: (roleId, upn) => save(() => api(session, `${base}/roles/${encodeURIComponent(roleId)}`, { method: "PUT", body: { upn } }), "Role saved."),
    onAdminAdd: (upn, displayName) => save(() => api(session, `${base}/roles/program-admin`, { method: "PUT", body: { upn, ...(displayName ? { displayName } : {}) } }), "Program admin added."),
    onAdminRemove: (upn) => save(() => api(session, `${base}/roles/program-admin/${encodeURIComponent(upn)}`, { method: "DELETE" }), "Program admin removed."),
    onRegister: (register) => save(() => api(session, `${base}/register`, { method: "PUT", body: { register } }), "Register saved."),
    onParameters: (parameters) => save(() => api(session, `${base}/parameters`, { method: "PUT", body: { parameters } }), "Parameters saved."),
    onRosterAdd: (upn, displayName) => save(() => api(session, `${base}/roster`, { method: "POST", body: { upn, ...(displayName ? { displayName } : {}) } }), "Added to the roster."),
    onRosterRemove: (upn) => save(() => api(session, `${base}/roster/${encodeURIComponent(upn)}`, { method: "DELETE" }), "Removed from the roster."),
    onSkills: (enabled) => save(() => api(session, `${base}/skills`, { method: "PUT", body: { enabled } }), enabled ? "Skills on." : "Skills off."),
    onGap: (description) => save(() => api(session, `${base}/gaps`, { method: "POST", body: { description } }), "Gap raised."),
    viewer: me.upn,
    peopleOf: async () => (await api<{ people: Person[] }>(session, `${base}/people`)).people,
    onAssign: (commitmentId, to) => save(() => api(session, `/api/commitments/${encodeURIComponent(commitmentId)}/assign`, { method: "POST", body: { to } }), "Assigned."),
    onLinkAdd: (kind, key, integrationId) => save(() => api(session, `${base}/links`, { method: "POST", body: { kind, key, ...(integrationId ? { integrationId } : {}) } }), "Link added."),
    onLinkRemove: (linkId) => save(() => api(session, `${base}/links/${encodeURIComponent(linkId)}`, { method: "DELETE" }), "Link removed."),
    instances,
    ...(card ? { structureCard: card } : {}),
    onGo: navigate,
  } : undefined;

  if (error) {
    if (error.status === 403 || error.status === 404) return <Hidden />;
    return <div class="state error">The program did not load.<div class="sub">{error.message}</div></div>;
  }
  if (!view) return <div class="state muted">Reading the program…</div>;
  return (
    <>
      <ProgramScreen view={view} admin={admin} />
    </>
  );
}

function StructureRoute({ session, id, me, onContract, onMeChanged }: { session: Session; id: string; me: Me | undefined; onContract: (c: Contract) => void; onMeChanged: () => Promise<void> }) {
  const [view, setView] = useState<StructurePage | undefined>(undefined);
  const [error, setError] = useState<{ status: number; message: string } | undefined>(undefined);
  const [notice, setNotice] = useState<{ text: string; error?: boolean } | undefined>(undefined);
  // Topics are the program's administrators' to edit. A viewer who cannot admin is not
  // shown an editor that would 403 on save (hide, do not announce: 22 September).
  const canAdmin = !!me && (me.isSponsor || (me.programs.find((p) => p.id === id)?.roleIds.length ?? 0) > 0);
  const [programTopics, setProgramTopics] = useState<TopicLine[] | undefined>(undefined);
  const [unitTopics, setUnitTopics] = useState<Record<string, TopicLine[]>>({});
  const loadTopics = async (unitId?: string) => {
    const at = `/api/programs/${encodeURIComponent(id)}`;
    if (unitId) {
      const r = await api<{ entries: TopicLine[] }>(session, `${at}/units/${encodeURIComponent(unitId)}/topics`).catch(() => undefined);
      if (r) setUnitTopics((u) => ({ ...u, [unitId]: r.entries }));
      return;
    }
    const r = await api<{ entries: TopicLine[] }>(session, `${at}/topics`).catch(() => undefined);
    if (r) setProgramTopics(r.entries);
  };
  useEffect(() => { if (canAdmin) void loadTopics(); }, [session, id, canAdmin]);
  const load = () => api<StructurePage>(session, `/api/programs/${encodeURIComponent(id)}/structure`).then((v) => { setView(v); setError(undefined); }, (e: ApiError) => setError({ status: e.status ?? 0, message: e.message }));
  useEffect(() => { void load(); }, [session, id]);
  useEffect(() => { if (view) onContract({ id, name: view.programName }); }, [view]);
  const save = async (fn: () => Promise<unknown>, text: string) => {
    try { await fn(); setNotice({ text }); await Promise.all([load(), onMeChanged()]); }
    catch (e) { setNotice({ text: e instanceof Error ? e.message : String(e), error: true }); }
  };
  const base = `/api/programs/${encodeURIComponent(id)}`;
  if (error) {
    if (error.status === 403 || error.status === 404) return <Hidden />;
    return <div class="state error">Structure did not load.<div class="sub">{error.message}</div></div>;
  }
  if (!view) return <div class="state muted">Reading the tree…</div>;
  return (
    <>
      <div class="body" style="flex-direction:column">
        <StructureScreen view={view} actions={{
          ...(notice ? { notice } : {}),
          onCreate: (input) => save(() => api(session, `${base}/units`, { method: "POST", body: input }), "Unit created."),
          onRole: (unitId, roleId, upn) => save(() => api(session, `${base}/units/${encodeURIComponent(unitId)}/roles/${encodeURIComponent(roleId)}`, { method: "PUT", body: { upn } }), "Role saved."),
          onAddPerson: (unitId, upn) => save(() => api(session, `${base}/units/${encodeURIComponent(unitId)}/members`, { method: "POST", body: { upn } }), "Placed on the unit."),
          onRemovePerson: (unitId, upn) => save(() => api(session, `${base}/units/${encodeURIComponent(unitId)}/members/${encodeURIComponent(upn)}`, { method: "DELETE" }), "Removed from the unit."),
          onVips: (unitId, entries) => save(() => api(session, `${base}/units/${encodeURIComponent(unitId)}/vips`, { method: "PUT", body: { entries } }), "VIPs saved."),
          onOptOut: (unitId, cadenceId, optedOut) => save(() => api(session, `${base}/units/${encodeURIComponent(unitId)}/opt-out`, { method: "PUT", body: { cadenceId, optedOut } }), optedOut ? "Opted out." : "Opt-out cleared."),
          onProgramVips: (entries) => save(() => api(session, `${base}/vips`, { method: "PUT", body: { entries } }), "VIPs saved for the contract."),
          onProgramOptOut: (cadenceId, optedOut) => save(() => api(session, `${base}/opt-out`, { method: "PUT", body: { cadenceId, optedOut } }), optedOut ? "Off for the whole contract." : "Back on for the contract."),
          onMove: (unitId, clinId) => save(() => api(session, `${base}/units/${encodeURIComponent(unitId)}/move`, { method: "POST", body: { clinId } }), "Workstream closed and opened under the CLIN."),
          onRename: (unitId, name) => save(() => api(session, `${base}/units/${encodeURIComponent(unitId)}`, { method: "PATCH", body: { name } }), "Renamed."),
          onClose: (unitId) => save(() => api(session, `${base}/units/${encodeURIComponent(unitId)}`, { method: "DELETE" }), "Closed."),
          onAssign: (unitId, commitmentId, to) => save(() => api(session, `/api/commitments/${encodeURIComponent(commitmentId)}/assign`, { method: "POST", body: { to, unitId } }), "Assigned onto this unit."),
          ...(canAdmin ? {
            ...(programTopics ? { programTopics } : {}),
            onProgramTopics: async (entries: TopicLine[]) => { await save(() => api(session, `${base}/topics`, { method: "PUT", body: { topics: entries } }), "Topics saved."); await loadTopics(); },
            unitTopics,
            onUnitSelected: (unitId: string) => { if (!unitTopics[unitId]) void loadTopics(unitId); },
            onUnitTopics: async (unitId: string, entries: TopicLine[]) => { await save(() => api(session, `${base}/units/${encodeURIComponent(unitId)}/topics`, { method: "PUT", body: { topics: entries } }), "Topics saved."); await loadTopics(unitId); },
          } : {}),
        }} {...(me?.upn ? { viewer: me.upn } : {})} />
      </div>
    </>
  );
}

/**
 * The contract's delivery model. Nothing here is a new route: the view and the model come
 * from the reads the contract page already makes, and the cadence switches from the
 * structure read that Structure makes. `/view` refuses anyone who may not administer the
 * contract, so a typed address gets the same nothing a hidden surface gets.
 */
function ModelRoute({ session, id, onContract }: { session: Session; id: string; onContract: (c: Contract) => void }) {
  const [view, setView] = useState<ProgramView | undefined>(undefined);
  const [model, setModel] = useState<PickerModel | undefined>(undefined);
  const [structure, setStructure] = useState<StructurePage | undefined>(undefined);
  const [error, setError] = useState<{ status: number; message: string } | undefined>(undefined);
  const [modelError, setModelError] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<{ text: string; error?: boolean } | undefined>(undefined);
  const [identities, setIdentities] = useState<IdentitiesView | undefined>(undefined);
  const base = `/api/programs/${encodeURIComponent(id)}`;
  const load = () => api<ProgramView>(session, `${base}/view`).then((v) => { setView(v); setError(undefined); }, (e: ApiError) => setError({ status: e.status ?? 0, message: e.message }));
  // A 403 here is the answer, not a failure: the read is refused to anybody who may not state one,
  // so the panel simply does not appear rather than appearing with controls that refuse.
  const loadIdentities = () => api<IdentitiesView>(session, `${base}/identities`).then(setIdentities, () => setIdentities(undefined));
  // The switches and the per-unit rows are only drawn once this read lands, so a store that
  // cannot answer it leaves the cadences listed and unswitchable rather than showing a
  // control that 503s.
  const loadStructure = () => api<StructurePage>(session, `${base}/structure`).then(setStructure, () => setStructure(undefined));
  useEffect(() => { void load(); void loadStructure(); void loadIdentities(); }, [session, id]);
  // Without the model there are no parameter kinds to draw, so a failure here is the page
  // failing. It keeps its own slot: the view's load clears `error` on success, and the two
  // reads do not land in a fixed order.
  useEffect(() => { api<PickerModel>(session, "/api/model").then(setModel, (e: Error) => setModelError(e.message)); }, [session]);
  useEffect(() => { if (view) onContract({ id, name: view.header.name }); }, [view]);
  const save = async (fn: () => Promise<unknown>, text: string, after: () => Promise<unknown> = load) => {
    try { await fn(); setNotice({ text }); await after(); }
    catch (e) { setNotice({ text: e instanceof Error ? e.message : String(e), error: true }); }
  };
  if (error) {
    if (error.status === 403 || error.status === 404) return <Hidden />;
    return <div class="state error">The delivery model did not load.<div class="sub">{error.message}</div></div>;
  }
  if (modelError) return <div class="state error">The delivery model did not load.<div class="sub">{modelError}</div></div>;
  if (!view || !model) return <div class="state muted">Reading the delivery model…</div>;
  return (
    <div class="body" style="flex-direction:column">
      <ModelScreen view={view} model={model}
        onParameters={(parameters) => save(() => api(session, `${base}/parameters`, { method: "PUT", body: { parameters } }), "Parameters saved.")}
        {...(identities ? { identities } : {})}
        {...(structure ? {
          programOptOuts: structure.programOptOuts,
          structure,
          onOptOut: (cadenceId: string, optedOut: boolean) => save(
            () => api(session, `${base}/opt-out`, { method: "PUT", body: { cadenceId, optedOut } }),
            optedOut ? "Off for the whole contract." : "Back on for the contract.",
            () => Promise.all([load(), loadStructure()])),
          // Not the shared `save`, which swallows the error: the editor has to know it failed so
          // it can stay open with what the admin typed still in it.
          onUnitCadence: async (unitId: string, cadenceId: string, edit: CadenceEdit) => {
            try {
              await api(session, `${base}/units/${encodeURIComponent(unitId)}/cadence`, { method: "PUT", body: { cadenceId, ...edit } });
            } catch (e) {
              setNotice({ text: e instanceof Error ? e.message : String(e), error: true });
              throw e;
            }
            setNotice({ text: "Saved how this unit runs it." });
            await Promise.all([load(), loadStructure()]);
          },
        } : {})}
        {...(notice ? { notice } : {})} />
    </div>
  );
}

interface AccountsView { unmapped: LinkedAccount[]; mapped: MappedAccount[]; roster: { upn: string; displayName: string }[] }

/**
 * Who may say that a name on a linked system, or an address, is a person: the same roles the
 * identity routes enforce. The control is the API's; this only decides whether to draw a link
 * into a page that would otherwise refuse.
 */
export function canStateIdentity(me: Me | undefined, programId: string): boolean {
  if (!me) return false;
  if (me.isSponsor) return true;
  return (me.programs.find((p) => p.id === programId)?.roleIds ?? []).some((r) => LEAD_ROLES.includes(r));
}

function RosterRoute({ session, id, me, onContract }: { session: Session; id: string; me: Me | undefined; onContract: (c: Contract) => void }) {
  const [view, setView] = useState<RosterPageView | undefined>(undefined);
  const [error, setError] = useState<{ status: number; message: string } | undefined>(undefined);
  const [accounts, setAccounts] = useState<AccountsView | undefined>(undefined);
  const loadAccounts = () => api<AccountsView>(session, `/api/programs/${encodeURIComponent(id)}/accounts`).then(setAccounts, () => setAccounts(undefined));
  useEffect(() => {
    api<RosterPageView>(session, `/api/programs/${encodeURIComponent(id)}/roster`).then((v) => { setView(v); setError(undefined); }, (e: ApiError) => setError({ status: e.status ?? 0, message: e.message }));
    void loadAccounts();
  }, [session, id]);
  useEffect(() => { if (view) onContract({ id, name: view.programName }); }, [view]);
  // A name links into a page the API refuses to anybody else, so it is a link only for the roles
  // that may state an identity.
  const canName = canStateIdentity(me, id);
  if (error) {
    if (error.status === 403 || error.status === 404) return <Hidden />;
    return <div class="state error">Roster did not load.<div class="sub">{error.message}</div></div>;
  }
  if (!view) return <div class="state muted">Reading the roster…</div>;
  return (
    <>
      <div class="body" style="flex-direction:column">
        <RosterPageScreen view={view}
          {...(canName ? { personHref: (upn: string) => personHref(id, upn) } : {})}
          accounts={accounts && <AccountsPanel programId={id} unmapped={accounts.unmapped} mapped={accounts.mapped} roster={accounts.roster} canName={canName} />} />
      </div>
    </>
  );
}

/**
 * One person on one contract: their account on each linked system, and their other addresses.
 * The read refuses anybody who may not state an identity, so a typed address gets the same
 * nothing a hidden surface gets.
 */
function PersonIdentityRoute({ session, id, upn, onContract }: { session: Session; id: string; upn: string; onContract: (c: Contract) => void }) {
  const [view, setView] = useState<PersonIdentityView | undefined>(undefined);
  const [error, setError] = useState<{ status: number; message: string } | undefined>(undefined);
  const [notice, setNotice] = useState<{ text: string; error?: boolean } | undefined>(undefined);
  const base = `/api/programs/${encodeURIComponent(id)}`;
  const at = `${base}/roster/${encodeURIComponent(upn)}/identity`;
  const load = () => api<PersonIdentityView>(session, at).then((v) => { setView(v); setError(undefined); }, (e: ApiError) => setError({ status: e.status ?? 0, message: e.message }));
  useEffect(() => { void load(); }, [session, at]);
  useEffect(() => { if (view) onContract({ id, name: view.programName }); }, [view]);
  const save = async (fn: () => Promise<unknown>, text: string) => {
    try { await fn(); setNotice({ text }); }
    catch (e) { setNotice({ text: e instanceof Error ? e.message : String(e), error: true }); }
    await load();
  };
  if (error) {
    if (error.status === 403 || error.status === 404) return <Hidden />;
    return <div class="state error">That person did not load.<div class="sub">{error.message}</div></div>;
  }
  if (!view) return <div class="state muted">Reading this person…</div>;
  return (
    <div class="body" style="flex-direction:column">
      <PersonIdentityScreen view={view}
        {...(accountOf(location.search) ? { preset: accountOf(location.search) } : {})}
        onSetAccount={(integrationId, externalId) => save(
          () => api(session, `${base}/accounts`, { method: "POST", body: { integrationId, externalId, upn } }),
          `${externalId} is now ${view.person.displayName} on that system.`)}
        onClearAccount={(integrationId, externalId) => save(
          () => api(session, `${base}/accounts/${encodeURIComponent(integrationId)}/${encodeURIComponent(externalId)}`, { method: "DELETE" }),
          `${externalId} is nobody's again. Nothing already written moved.`)}
        onAddAddress={(value) => save(
          () => api(session, `${base}/identities`, { method: "POST", body: { kind: "email", value, upn } }),
          `${value} now lands on this record.`)}
        onRemoveAddress={(value) => save(
          () => api(session, `${base}/identities/email/${encodeURIComponent(value)}`, { method: "DELETE" }),
          `${value} no longer folds. Nothing already written moved.`)}
        {...(notice ? { notice } : {})} />
    </div>
  );
}

function UnitWeekRoute({ session, id, unitId, me, onMeChanged }: { session: Session; id: string; unitId: string; me: Me | undefined; onMeChanged: () => Promise<void> }) {
  const [view, setView] = useState<UnitView | undefined>(undefined);
  const [error, setError] = useState<{ status: number; message: string } | undefined>(undefined);
  const [notice, setNotice] = useState<{ text: string; error?: boolean } | undefined>(undefined);
  const load = () => api<UnitView>(session, `/api/programs/${encodeURIComponent(id)}/units/${encodeURIComponent(unitId)}/view`).then((v) => { setView(v); setError(undefined); }, (e: ApiError) => setError({ status: e.status ?? 0, message: e.message }));
  useEffect(() => { void load(); }, [session, id, unitId]);
  if (error) {
    if (error.status === 403 || error.status === 404) return <Hidden />;
    return <div class="state error">This week did not load.<div class="sub">{error.message}</div></div>;
  }
  if (!view) return <div class="state muted">Reading this unit…</div>;
  const canAdmin = !!me && (me.isSponsor || (me.programs.find((p) => p.id === id)?.roleIds.length ?? 0) > 0);
  const assign = me ? {
    viewer: me.upn,
    peopleOf: async () => (await api<{ people: Person[] }>(session, `/api/programs/${encodeURIComponent(id)}/people`)).people,
    onAssign: async (commitmentId: string, to: string) => {
      try {
        await api(session, `/api/commitments/${encodeURIComponent(commitmentId)}/assign`, { method: "POST", body: { to, unitId } });
        setNotice({ text: "Assigned onto this unit." });
        await Promise.all([load(), onMeChanged()]);
      } catch (e) { setNotice({ text: e instanceof Error ? e.message : String(e), error: true }); }
    },
    hint: "They accept before it enters their record. This places the ask on this unit.",
    label: "Assign onto this unit…",
  } : undefined;
  return (
    <>
      {notice && <div class={`notice${notice.error ? " error" : ""}`} style="margin:12px 36px 0">{notice.text}</div>}
      <UnitWeekScreen view={view} onGo={navigate} canAdmin={canAdmin} {...(assign ? { assign } : {})} />
    </>
  );
}

function PortfolioPage({ session }: { session: Session }) {
  const [view, setView] = useState<PortfolioResponse | undefined>(undefined);
  const [error, setError] = useState<{ status: number; message: string } | undefined>(undefined);
  useEffect(() => { api<PortfolioResponse>(session, "/api/portfolio").then(setView, (e: ApiError) => setError({ status: e.status ?? 0, message: e.message })); }, [session]);
  if (error) {
    if (error.status === 403 || error.status === 404) return <Hidden />;
    return <div class="state error">The portfolio did not load.<div class="sub">{error.message}</div></div>;
  }
  if (!view) return <div class="state muted">Reading the portfolio…</div>;
  return <PortfolioScreen view={view} programHref={(id) => `/app/programs/${encodeURIComponent(id)}`} onNavigate={navigate} />;
}

function InitiativesPage({ session, me }: { session: Session; me: Me | undefined }) {
  const [entries, setEntries] = useState<InitiativeSummary[] | undefined>(undefined);
  const [error, setError] = useState<{ status: number; message: string } | undefined>(undefined);
  const [notice, setNotice] = useState<{ text: string; error?: boolean } | undefined>(undefined);
  const load = () => api<{ initiatives: InitiativeSummary[] }>(session, "/api/initiatives").then((r) => { setEntries(r.initiatives); setError(undefined); }, (e: ApiError) => setError({ status: e.status ?? 0, message: e.message }));
  useEffect(() => { void load(); }, [session]);
  // You may only start one on a contract you lead: a role is not the same as steering.
  const mine = (me?.programs ?? []).filter((p) => (p.roleIds ?? []).some((r) => LEAD_ROLES.includes(r))).map((p) => ({ id: p.id, name: p.name }));
  // Answers whether it landed. A handler that resolved either way left the screen
  // clearing a form it had just failed to save.
  const onCreate = async (input: { name: string; programId: string; outcome?: string }): Promise<boolean> => {
    try { await api(session, "/api/initiatives", { method: "POST", body: input }); setNotice({ text: "Started." }); await load(); return true; }
    catch (e) { setNotice({ text: e instanceof Error ? e.message : String(e), error: true }); return false; }
  };
  if (error) {
    if (error.status === 403 || error.status === 404) return <Hidden />;
    return <div class="state error">Initiatives did not load.<div class="sub">{error.message}</div></div>;
  }
  if (!entries) return <div class="state muted">Reading initiatives…</div>;
  return <InitiativesScreen entries={entries} programs={mine} onOpen={(id) => navigate(`/app/initiatives/${encodeURIComponent(id)}`)} {...(mine.length ? { onCreate } : {})} notice={notice} />;
}

function InitiativePage({ session, id, me }: { session: Session; id: string; me: Me | undefined }) {
  const [view, setView] = useState<InitiativeDetail | undefined>(undefined);
  const [error, setError] = useState<{ status: number; message: string } | undefined>(undefined);
  const load = () => api<InitiativeDetail>(session, `/api/initiatives/${encodeURIComponent(id)}`).then((v) => { setView(v); setError(undefined); }, (e: ApiError) => setError({ status: e.status ?? 0, message: e.message }));
  useEffect(() => { void load(); }, [session, id]);
  const [notice, setNotice] = useState<{ text: string; error?: boolean } | undefined>(undefined);
  const at = `/api/initiatives/${encodeURIComponent(id)}`;
  // Every change on this page reports itself. A refusal used to reject into nothing, so
  // "point at it" looked like a button that did not work.
  const say = (done: string) => async (run: Promise<unknown>): Promise<boolean> => {
    try { await run; setNotice({ text: done }); await load(); return true; }
    catch (e) { setNotice({ text: e instanceof Error ? e.message : String(e), error: true }); return false; }
  };
  const onStatus = async (status: string) => { await say(`Marked ${status}.`)(api(session, at, { method: "PATCH", body: { status } })); };
  const mine = (me?.programs ?? []).filter((p) => (p.roleIds ?? []).some((r) => LEAD_ROLES.includes(r))).map((p) => ({ id: p.id, name: p.name }));
  const onAttach = async (programId: string) => { await say("Contract put in.")(api(session, `${at}/programs/${encodeURIComponent(programId)}`, { method: "PUT" })); };
  const onDetach = async (programId: string) => { await say("Contract taken out.")(api(session, `${at}/programs/${encodeURIComponent(programId)}`, { method: "DELETE" })); };
  const onAddSource = (input: { programId: string; integrationId: string; kind: string; query: string }) =>
    say("Pointed at it. Trellis reads the counts within fifteen minutes.")(api(session, `${at}/sources`, { method: "POST", body: input }));
  const onRemoveSource = async (sourceId: string) => { await say("Pointer removed.")(api(session, `${at}/sources/${encodeURIComponent(sourceId)}`, { method: "DELETE" })); };
  const onAddLink = (input: { programId: string; key: string }) =>
    say("Linked. Trellis reads it within ten minutes.")(api(session, `${at}/links`, { method: "POST", body: { ...input, kind: "teams-team" } }));
  const onRemoveLink = async (linkId: string) => { await say("Link removed.")(api(session, `${at}/links/${encodeURIComponent(linkId)}`, { method: "DELETE" })); };
  // Only the ticket instances this contract actually links: offering one it does not
  // would look valid and then be refused.
  const instancesOf = async (programId: string) => {
    const [links, all] = await Promise.all([
      api<{ links: { kind: string; instance: string }[] }>(session, `/api/programs/${encodeURIComponent(programId)}/links`),
      api<{ integrations: { id: string; displayName: string; kind: string }[] }>(session, "/api/integrations"),
    ]);
    const linked = new Set(links.links.filter((l) => l.kind === "jira-project").map((l) => l.instance));
    return all.integrations.filter((i) => i.kind === "jira" && linked.has(i.displayName));
  };
  if (error) {
    if (error.status === 403 || error.status === 404) return <Hidden />;
    return <div class="state error">That initiative did not load.<div class="sub">{error.message}</div></div>;
  }
  if (!view) return <div class="state muted">Reading the initiative…</div>;
  return <InitiativeScreen view={view} onBack={() => navigate("/app/initiatives")} onStatus={onStatus} allPrograms={mine} onAttach={onAttach} onDetach={onDetach} onAddSource={onAddSource} onRemoveSource={onRemoveSource} onAddLink={onAddLink} onRemoveLink={onRemoveLink} instancesOf={instancesOf} notice={notice} />;
}

function TopicsPage({ session }: { session: Session }) {
  const [entries, setEntries] = useState<TopicLine[] | undefined>(undefined);
  const [error, setError] = useState<{ status: number; message: string } | undefined>(undefined);
  const [notice, setNotice] = useState<{ text: string; error?: boolean } | undefined>(undefined);
  const load = () => api<{ entries: TopicLine[] }>(session, "/api/topics").then((r) => { setEntries(r.entries); setError(undefined); }, (e: ApiError) => setError({ status: e.status ?? 0, message: e.message }));
  useEffect(() => { void load(); }, [session]);
  const onSave = async (next: TopicLine[]) => {
    try {
      const r = await api<{ entries: TopicLine[] }>(session, "/api/topics", { method: "PUT", body: { topics: next } });
      setEntries(r.entries);
      setNotice({ text: "Saved. New mail is read against these." });
    } catch (e) { setNotice({ text: e instanceof Error ? e.message : String(e), error: true }); }
  };
  if (error) {
    if (error.status === 403 || error.status === 404) return <Hidden />;
    return <div class="state error">Topics did not load.<div class="sub">{error.message}</div></div>;
  }
  if (!entries) return <div class="state muted">Reading topics…</div>;
  return <TopicsScreen entries={entries} onSave={onSave} notice={notice} />;
}

function VipsPage({ session }: { session: Session }) {
  const [entries, setEntries] = useState<VipLine[] | undefined>(undefined);
  const [error, setError] = useState<{ status: number; message: string } | undefined>(undefined);
  const [notice, setNotice] = useState<{ text: string; error?: boolean } | undefined>(undefined);
  const load = () => api<{ entries: VipLine[] }>(session, "/api/vips").then((r) => { setEntries(r.entries); setError(undefined); }, (e: ApiError) => setError({ status: e.status ?? 0, message: e.message }));
  useEffect(() => { void load(); }, [session]);
  const onSave = async (next: VipLine[]) => {
    try {
      const r = await api<{ entries: VipLine[] }>(session, "/api/vips", { method: "PUT", body: { entries: next } });
      setEntries(r.entries);
      setNotice({ text: "Corporate VIPs are saved." });
    } catch (e) { setNotice({ text: e instanceof Error ? e.message : String(e), error: true }); }
  };
  if (error) {
    if (error.status === 403 || error.status === 404) return <Hidden />;
    return <div class="state error">VIPs did not load.<div class="sub">{error.message}</div></div>;
  }
  if (!entries) return <div class="state muted">Reading corporate VIPs…</div>;
  return <VipsScreen entries={entries} onSave={onSave} notice={notice} />;
}

/**
 * The operations page. The window lives in this component rather than the URL: it is a way
 * of looking at the same page, not a different address, and a deep link to a fortnight is
 * not a thing anybody sends anybody.
 */
function OpsPage({ session }: { session: Session }) {
  const [days, setDays] = useState(14);
  const [view, setView] = useState<OpsScreenView | undefined>(undefined);
  const [error, setError] = useState<{ status: number; message: string } | undefined>(undefined);
  useEffect(() => {
    let live = true;
    api<OpsScreenView>(session, `/api/ops/pipeline?days=${days}`).then(
      (v) => { if (live) { setView(v); setError(undefined); } },
      (e: ApiError) => { if (live) setError({ status: e.status ?? 0, message: e.message }); },
    );
    return () => { live = false; };
  }, [session, days]);
  if (error) {
    if (error.status === 403 || error.status === 404) return <Hidden />;
    return <div class="state error">Operations did not load.<div class="sub">{error.message}</div></div>;
  }
  if (!view) return <div class="state muted">Counting…</div>;
  return <OpsScreen view={view} onWindow={setDays} />;
}

function IntegrationsPage({ session }: { session: Session }) {
  const [entries, setEntries] = useState<IntegrationLine[] | undefined>(undefined);
  const [error, setError] = useState<{ status: number; message: string } | undefined>(undefined);
  const [notice, setNotice] = useState<{ text: string; error?: boolean } | undefined>(undefined);
  const load = () => api<{ integrations: IntegrationLine[] }>(session, "/api/integrations").then((r) => { setEntries(r.integrations); setError(undefined); }, (e: ApiError) => setError({ status: e.status ?? 0, message: e.message }));
  useEffect(() => { void load(); }, [session]);
  const onAdd = async (input: { kind: string; deployment: string; baseUrl: string; displayName: string; credential: string }): Promise<boolean> => {
    try {
      await api(session, "/api/integrations", { method: "POST", body: input });
      setNotice({ text: "Instance saved. The credential is in Key Vault." });
      await load();
      return true;
    } catch (e) { setNotice({ text: e instanceof Error ? e.message : String(e), error: true }); return false; }
  };
  const onEdit = async (id: string, input: IntegrationEdit): Promise<boolean> => {
    try {
      await api(session, `/api/integrations/${id}`, { method: "PATCH", body: input });
      setNotice({ text: input.credential ? "Instance saved. The new credential is in Key Vault." : "Instance saved." });
      await load();
      return true;
    } catch (e) { setNotice({ text: e instanceof Error ? e.message : String(e), error: true }); return false; }
  };
  const onRemove = async (id: string): Promise<boolean> => {
    try {
      await api(session, `/api/integrations/${id}`, { method: "DELETE" });
      setNotice({ text: "Instance removed." });
      await load();
      return true;
    } catch (e) { setNotice({ text: e instanceof Error ? e.message : String(e), error: true }); return false; }
  };
  const onTest = async (id: string) => {
    try {
      const r = await api<{ account?: { displayName?: string; email?: string; externalId: string } }>(session, `/api/integrations/${id}/test`, { method: "POST" });
      const who = r.account;
      setNotice({ text: who ? `It answers as ${who.displayName ?? who.externalId}${who.email ? ` (${who.email})` : ""}.` : "It answers." });
      await load();
    } catch (e) { setNotice({ text: e instanceof Error ? e.message : String(e), error: true }); await load(); }
  };
  if (error) {
    if (error.status === 403 || error.status === 404) return <Hidden />;
    return <div class="state error">Instances did not load.<div class="sub">{error.message}</div></div>;
  }
  if (!entries) return <div class="state muted">Reading instances…</div>;
  return <IntegrationsScreen entries={entries} onAdd={onAdd} onEdit={onEdit} onRemove={onRemove} onTest={onTest} notice={notice} />;
}

/** The Trellis mark from the Teams app, served beside the bundle. */
/** A draft, whole: the person reads it here and sends it themselves. Nothing on this page sends anything. */
function DraftPage({ session, id, artifactRowId }: { session: Session; id: string; artifactRowId: string }) {
  const [draft, setDraft] = useState<DraftView | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [notice, setNotice] = useState<string | undefined>();
  const path = `/api/programs/${encodeURIComponent(id)}/artifacts/${encodeURIComponent(artifactRowId)}`;
  const load = () => api<DraftView>(session, path).then(setDraft, (e: ApiError) => setError(e.message));
  useEffect(() => { void load(); }, [session, id, artifactRowId]);
  const approve = async () => {
    try { await api(session, `${path}/approve`, { method: "POST" }); setNotice("Approved. Still not sent."); await load(); }
    catch (e) { setNotice(e instanceof Error ? e.message : String(e)); }
  };
  if (error) return <div class="state">{error}</div>;
  if (!draft) return <div class="state">Loading.</div>;
  return (
    <DraftScreen
      draft={draft}
      programId={id}
      notice={notice}
      onDownload={() => {
        const blob = new Blob([draft.body], { type: "text/markdown" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `${draft.artifactId}-v${draft.version}.md`; a.click();
        URL.revokeObjectURL(url);
      }}
      onApprove={draft.canApprove && draft.status !== "approved" ? () => void approve() : undefined}
    />
  );
}
